import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download } from "@phosphor-icons/react";
import { format } from "date-fns";
import { downloadCsv } from "@/utils/downloadCsv";
import {
  Loader,
  MiniTile,
  DeltaBadge,
  EmployeeBlock,
  useReport,
  addDays,
} from "../shared";

export const EmployeeDetailsSection = ({ filter }) => {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Fetch the user roster once so the filter dropdown is populated even
  // before any report rows come back.
  useEffect(() => {
    axios.get(`${API}/users`)
      .then((r) => setUsers(r.data || []))
      .catch(() => { /* non-fatal */ });
  }, []);

  const [data, loading, prevData] = useReport(
    "/reports/employee-details",
    filter,
    false,
    {
      inclusiveEnd: true,
      extraParams: selectedUserId ? { user_id: selectedUserId } : undefined,
    }
  );

  // When a single employee is selected, expand their block by default.
  useEffect(() => {
    if (selectedUserId && data?.employees?.length === 1) {
      setExpanded({ [data.employees[0].user_id]: true });
    }
  }, [selectedUserId, data]);

  const toggle = (uid) => setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }));
  const expandAll = () =>
    setExpanded(Object.fromEntries((data?.employees || []).map((e) => [e.user_id, true])));
  const collapseAll = () => setExpanded({});

  const exportCsv = (extraQs = {}) => {
    const qs = new URLSearchParams({
      start_date: format(filter.start, "yyyy-MM-dd"),
      end_date: format(addDays(filter.end, -1), "yyyy-MM-dd"),
      ...extraQs,
    });
    if (filter.startTime) qs.set("start_time", filter.startTime);
    if (filter.endTime) qs.set("end_time", filter.endTime);
    if (filter.weekdays?.length && filter.weekdays.length < 7) {
      qs.set("weekdays", [...filter.weekdays].sort((a, b) => a - b).join(","));
    }
    downloadCsv(`${API}/reports/employee-details/export.csv?${qs.toString()}`);
  };

  return (
    <div className="space-y-3" data-testid="employee-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Employee:</span>
          <div className="w-56">
            <Select
              value={selectedUserId || "__all__"}
              onValueChange={(v) => setSelectedUserId(v === "__all__" ? "" : v)}
            >
              <SelectTrigger data-testid="employee-filter-select">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All employees</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`employee-option-${u.id}`}>
                    {u.name} — {u.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!selectedUserId && data?.employees?.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={expandAll} data-testid="employee-expand-all">
              Expand all
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll} data-testid="employee-collapse-all">
              Collapse all
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => exportCsv()}
              data-testid="employee-export-csv-btn"
            >
              <Download size={14} /> Export CSV
            </Button>
          </div>
        )}
        {selectedUserId && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => exportCsv({ user_id: selectedUserId })}
            data-testid="employee-export-csv-btn"
          >
            <Download size={14} /> Export CSV
          </Button>
        )}
      </div>

      {loading && <Loader />}

      {!loading && data && data.employees.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground rounded-lg bg-secondary/30">
          No timeclock or sales data for this employee in the selected window.
        </div>
      )}

      {!loading && data && data.employees.length > 0 && (
        <>
          {!selectedUserId && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <MiniTile
                label="Hours"
                value={data.totals.hours.toFixed(2)}
                testId="emp-total-hours"
                extra={prevData ? <DeltaBadge current={data.totals.hours} previous={prevData.totals.hours} /> : null}
              />
              <MiniTile
                label="Total Pay"
                value={`$${data.totals.total_pay.toFixed(2)}`}
                tone="text-accent"
                testId="emp-total-pay"
                extra={prevData ? <DeltaBadge current={data.totals.total_pay} previous={prevData.totals.total_pay} /> : null}
              />
              <MiniTile
                label="Sales"
                value={`$${data.totals.sales.toFixed(2)}`}
                testId="emp-total-sales"
                extra={prevData ? <DeltaBadge current={data.totals.sales} previous={prevData.totals.sales} /> : null}
              />
              <MiniTile
                label="Orders"
                value={data.totals.orders}
                testId="emp-total-orders"
                extra={prevData ? <DeltaBadge current={data.totals.orders} previous={prevData.totals.orders} /> : null}
              />
            </div>
          )}

          <div className="space-y-2">
            {data.employees.map((e) => {
              const isOpen = !!expanded[e.user_id] || !!selectedUserId;
              return (
                <EmployeeBlock
                  key={e.user_id}
                  emp={e}
                  isOpen={isOpen}
                  onToggle={() => toggle(e.user_id)}
                  showToggle={!selectedUserId}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
